import { Card, CardHeader, CardTitle, CardContent } from '@airclou/ui';
import { useTranslation } from 'react-i18next';
import { ActivityItem } from '../../api/overview';
import {
  CreditCard,
  Users,
  TrendingUp,
  Package,
  Shield,
  Sparkles,
  Settings,
  Building2,
  UserCircle,
  Image,
  Circle,
  type LucideIcon,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

// Keyed by the audit log's `category`, which is open-ended -- hence the index
// signature and the Circle fallback at the call site. The first four are the
// categories the dashboard actually writes today; the rest predate the feed
// being wired to real audit rows and are kept so nothing regresses if they
// start being emitted.
const activityIcons: Record<string, LucideIcon> = {
  settings: Settings,
  workspaces: Building2,
  profiles: UserCircle,
  logos: Image,
  billing: CreditCard,
  team: Users,
  usage: TrendingUp,
  subscription: Package,
  security: Shield,
  feature: Sparkles,
};

export interface ActivityFeedProps {
  activities: ActivityItem[];
  onViewAll?: () => void;
}

export function ActivityFeed({ activities, onViewAll }: ActivityFeedProps) {
  const { t } = useTranslation('dashboard');
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">{t('activityFeed.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {activities.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
              {t('activityFeed.noRecentActivity')}
            </p>
          ) : (
            <>
              {activities.map((activity) => {
                const Icon = activityIcons[activity.type] || Circle;
                return (
                  <div key={activity.id} className="flex items-start space-x-3">
                    <div className="flex-shrink-0 mt-1">
                      <Icon className="h-4 w-4 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-foreground">
                        {activity.description}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {formatDistanceToNow(new Date(activity.timestamp), { addSuffix: true })}
                      </p>
                    </div>
                  </div>
                );
              })}
              {onViewAll && (
                <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                  <button
                    onClick={onViewAll}
                    className="text-sm font-medium text-primary hover:text-[hsl(var(--primary-hover))]"
                  >
                    {t('activityFeed.viewAll')}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
